import { lazy, Suspense, type ReactElement } from "react";
import {
  createHashHistory,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "./components/AppShell.js";
import { RouteSkeleton } from "./components/RouteSkeleton.js";
import { AdsPage } from "./routes/AdsPage.js";
import { LoginPage } from "./routes/LoginPage.js";
import { DEFAULT_ADS_SEARCH, adsSearchToQueryString, parseAdsSearch } from "./lib/urlState.js";
import {
  AD_MANAGEMENT_SEARCH_KEYS,
  adManagementSearchToQueryString,
  defaultAdManagementSearch,
  parseAdManagementSearch,
} from "./lib/adManagementState.js";
import {
  STYLE_CODE_BOARD_SEARCH_KEYS,
  parseStyleCodeBoardSearch,
  styleCodeBoardSearchToQueryString,
} from "./lib/styleCodeBoardSearch.js";
import {
  OVERVIEW_SEARCH_KEYS,
  overviewSearchToQueryString,
  parseOverviewSearch,
} from "./lib/overviewState.js";
import {
  SPEND_OVERVIEW_SEARCH_KEYS,
  parseSpendOverviewSearch,
  spendOverviewSearchToQueryString,
} from "./lib/spendOverviewState.js";
import {
  GOOGLE_CAMPAIGNS_SEARCH_KEYS,
  googleCampaignsSearchToQueryString,
  parseGoogleCampaignsSearch,
} from "./lib/googleCampaignsSearch.js";
import { useUiStore } from "./store/uiStore.js";
import { FeatureGuard } from "./components/FeatureGuard.js";
import type { FeatureKey } from "@ams/contracts";

/**
 * 路由级功能守卫（C1 Phase 3）：未授权页面直接输 URL 时渲染**明确的权限页**。
 *
 * feature↔页面映射**只有一处**（`lib/permissions.ts` 的 `PAGE_FEATURES`），这里只做绑定。
 */
function guarded(feature: FeatureKey, Component: () => ReactElement): () => ReactElement {
  return function GuardedRoute(): ReactElement {
    return (
      <FeatureGuard feature={feature}>
        <Component />
      </FeatureGuard>
    );
  };
}

/**
 * 路由表：全部集中在这里，页面组件只导出组件。
 *
 * 代码分割策略（对应性能预算里的「路由切换 < 150ms」与「首屏 JS < 200KB」）：
 *   - **首屏路由（广告指标表）保持同步导入**：它是落地页，把它也拆出去只会多一次
 *     网络往返，反而拖慢 TTI/FCP。拆分的收益在「首屏不需要的路由」。
 *   - 其余三个路由用 React.lazy 拆成独立 chunk，并且用 route.loader 做
 *     **意图预取**（defaultPreload: "intent" → 悬停/聚焦侧边栏时就开始下载），
 *     因此「点了就开」的手感不会因为懒加载而变差。
 *   - Suspense 的 fallback 是固定尺寸骨架（见 RouteSkeleton），不产生 CLS。
 *
 * 懒加载组件用稳定的函数组件包一层：既避开 lazy 组件的类型差异，
 * 也保证组件标识稳定（不会因为重新渲染而卸载/重挂载子树）。
 */

const OverviewPageLazy = lazy(() => import("./routes/OverviewPage.js").then((mod) => ({ default: mod.OverviewPage })));
const SyncPageLazy = lazy(() => import("./routes/SyncPage.js").then((mod) => ({ default: mod.SyncPage })));
const ProductsPageLazy = lazy(() =>
  import("./routes/ProductsPage.js").then((mod) => ({ default: mod.ProductsPage })),
);
const ProductCategoriesPageLazy = lazy(() =>
  import("./routes/ProductCategoriesPage.js").then((mod) => ({ default: mod.ProductCategoriesPage })),
);
const AdManagementPageLazy = lazy(() =>
  import("./routes/AdManagementPage.js").then((mod) => ({ default: mod.AdManagementPage })),
);
const StyleCodeBoardPageLazy = lazy(() =>
  import("./routes/StyleCodeBoardPage.js").then((mod) => ({ default: mod.StyleCodeBoardPage })),
);
// 素材看板（task-23 · C2）：店铺级看板，同样按需拆包 + 意图预取。
const CreativeBoardPageLazy = lazy(() =>
  import("./routes/CreativeBoardPage.js").then((mod) => ({ default: mod.CreativeBoardPage })),
);
const SpendOverviewPageLazy = lazy(() =>
  import("./routes/SpendOverviewPage.js").then((mod) => ({ default: mod.SpendOverviewPage })),
);
const GoogleCampaignsPageLazy = lazy(() =>
  import("./routes/GoogleCampaignsPage.js").then((mod) => ({ default: mod.GoogleCampaignsPage })),
);

function OverviewRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <OverviewPageLazy />
    </Suspense>
  );
}

function SyncRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <SyncPageLazy />
    </Suspense>
  );
}

function ProductsRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <ProductsPageLazy />
    </Suspense>
  );
}

function ProductCategoriesRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <ProductCategoriesPageLazy />
    </Suspense>
  );
}

function AdManagementRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <AdManagementPageLazy />
    </Suspense>
  );
}

function StyleCodeBoardRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <StyleCodeBoardPageLazy />
    </Suspense>
  );
}

function CreativeBoardRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <CreativeBoardPageLazy />
    </Suspense>
  );
}

function SpendOverviewRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <SpendOverviewPageLazy />
    </Suspense>
  );
}

function GoogleCampaignsRouteComponent(): ReactElement {
  return (
    <Suspense fallback={<RouteSkeleton variant="cards" />}>
      <GoogleCampaignsPageLazy />
    </Suspense>
  );
}

const rootRoute = createRootRoute({ component: AppShell });

const adsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  // URL 是筛选/排序/分页的唯一真相；解析对垃圾输入免疫（见 lib/urlState.ts）
  validateSearch: (search: Record<string, unknown>) => parseAdsSearch(search),
  component: guarded("ad_metrics", AdsPage),
});

const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overview",
  // 时间范围进 URL（可分享 / 刷新还原）；默认最近 30 天且**不含今天**
  // （2026-10-05 第十五批 · 用户第 3 条）。区间算法与广告管理**共用** `dateRange.ts`。
  validateSearch: (search: Record<string, unknown>) => parseOverviewSearch(search),
  // 这个 loader 的唯一作用是让 defaultPreload="intent" 有东西可预取
  loader: () => import("./routes/OverviewPage.js"),
  component: guarded("overview", OverviewRouteComponent),
});

const syncRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sync",
  loader: () => import("./routes/SyncPage.js"),
  component: guarded("sync", SyncRouteComponent),
});


/**
 * `/settings` 深链（用户第三轮第 4 条：**深链仍要能用**）。
 *
 * 选的是任务里给的方案 A —— **保留路由，让它打开弹窗并回退到底层页面**：
 *  · `beforeLoad` 里先把全局的 `settingsOpen` 打开（弹窗挂在外壳上，跨路由不卸载），
 *    再 `redirect` 到广告管理（用户的工作页，也是导航里的默认落点）；
 *  · `replace: true`：把 `/settings` 这条历史记录**换掉**而不是压栈，
 *    所以关闭弹窗后按浏览器「后退」不会被弹回一个又立刻重定向的 `/settings`（不会成环）；
 *  · 为什么不用「渲染一个『打开设置』的页面」：那会多一次跳转、还要求用户再点一下；
 *    而直接重定向 + 弹窗与「点左下角设置」的最终状态**完全一致**（同一个 store 开关）。
 *
 * 代价（如实记录）：直接粘 `/settings` 进来时，浏览器里没有「原页面」可回退，
 * 因此落到广告管理页；在应用内则不会走到这条路由（入口是 `<button>`，不导航）。
 */
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: () => {
    useUiStore.getState().setSettingsOpen(true);
    // TanStack Router 的约定：beforeLoad 里 throw 一个 redirect 来改道。
    throw redirect({ to: "/ad-management", search: defaultAdManagementSearch(), replace: true });
  },
});

// 商品库（货号维度）与分类树配置：分类树配置从商品页的按钮进入，不占主导航项。
const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  loader: () => import("./routes/ProductsPage.js"),
  component: guarded("products", ProductsRouteComponent),
});

const productCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products/categories",
  loader: () => import("./routes/ProductCategoriesPage.js"),
  component: guarded("products", ProductCategoriesRouteComponent),
});

// 货号看板（站点级；金额默认 USD 折算，可切回原币）。
// R24：**语言筛选进 URL**（可分享、刷新还原）；其余筛选仍在页面内状态。
const styleCodeBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/style-codes",
  validateSearch: (search: Record<string, unknown>) => parseStyleCodeBoardSearch(search),
  loader: () => import("./routes/StyleCodeBoardPage.js"),
  component: guarded("style_code_board", StyleCodeBoardRouteComponent),
});

/**
 * 素材看板（task-23 · C2）：一行 = 一个素材名（广告名）；「重复在投」展开到广告粒度。
 * 顶部只有品牌 + 站点（跟随全局归属栏），**没有时间范围**（窗口由后端按已结算业务日推导）；
 * Tab / 排序 / 行数 / 勾选都是页面内状态，因此本路由**不注册 validateSearch**。
 */
const creativeBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/creatives",
  loader: () => import("./routes/CreativeBoardPage.js"),
  component: guarded("creative_board", CreativeBoardRouteComponent),
});

/**
 * 花费总览（2026-10-05 第十九批 · 用户第 2 条）：按月 / 按天看 Meta / Google 花费
 * 与销售额占比。筛选（粒度 / 窗口 / 店铺 / 作用域）全部进 URL（可分享、刷新还原）。
 */
const spendOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/spend-overview",
  validateSearch: (search: Record<string, unknown>) => parseSpendOverviewSearch(search),
  loader: () => import("./routes/SpendOverviewPage.js"),
  component: guarded("spend_overview", SpendOverviewRouteComponent),
});

// 广告管理（折叠树 + 三分组）：筛选写进 URL（默认值不写、垃圾输入回落）。
const adManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ad-management",
  validateSearch: (search: Record<string, unknown>) => parseAdManagementSearch(search),
  loader: () => import("./routes/AdManagementPage.js"),
  component: guarded("ad_management", AdManagementRouteComponent),
});

/**
 * Google 系列（2026-10-05 第二十批）：Google Ads 系列层管理（基础版）。
 * 列表来自结构快照（「同步系列」刷新）；写入走 Google Ads API mutate（独立开关）。
 * 页面把筛选放在页面内状态（站点筛选含「归属未定」哨兵，不进 URL/全局 store）。
 */
const googleCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/google-campaigns",
  // 第二十五批：概览 / 明细（`gview`）与系列名搜索（`gq`）进 URL（可分享、刷新还原）。
  // key 带 `g` 前缀 → 与其它页的 key 集合互斥，`stringifySearch` 不会串页。
  validateSearch: (search: Record<string, unknown>) => parseGoogleCampaignsSearch(search),
  loader: () => import("./routes/GoogleCampaignsPage.js"),
  component: guarded("google_campaigns", GoogleCampaignsRouteComponent),
});

/**
 * 登录路由：**同样挂在 rootRoute 下**，但 AppShell 对 `/login` 走「无 chrome 的裸渲染」，
 * 因此登录页不会出现侧边栏/顶栏，也不会被路由守卫重定向（守卫本身跳过 /login）。
 */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect: string } => ({
    redirect: typeof search["redirect"] === "string" ? search["redirect"] : "/",
  }),
  component: LoginPage,
});

const routeTree = rootRoute.addChildren([
  adsRoute,
  overviewRoute,
  spendOverviewRoute,
  syncRoute,
  settingsRoute,
  productsRoute,
  productCategoriesRoute,
  styleCodeBoardRoute,
  creativeBoardRoute,
  adManagementRoute,
  googleCampaignsRoute,
  loginRoute,
]);

/**
 * 预览构建用 **hash 路由**（`#/overview`）。
 *
 * 为什么需要：把应用打包成**单文件 HTML** 后，它可能被 `file://` 打开、或放在
 * 一个不做 history fallback 的沙箱 iframe 里预览。浏览器 history 模式下
 * `/overview` 这类深链需要服务器兜底返回 index.html，没有服务器就是白屏；
 * hash 模式只依赖 `location.hash`，到哪都能跑。
 *
 * 生产构建不受影响：不设这个环境变量时仍然是默认的浏览器 history。
 */
const useHashHistory = import.meta.env["VITE_AMS_HASH_ROUTER"] === "1";

export const router = createRouter({
  routeTree,
  ...(useHashHistory ? { history: createHashHistory() } : {}),
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: RouteSkeleton,
  /**
   * 查询参数序列化。`/` 的搜索对象是 AdsSearch（默认值不写入 URL）；
   * 其它路由（例如 `/login?redirect=...`）走通用序列化 —— 这个分支是必需的：
   * stringifySearch 无法感知路由，若一律按 AdsSearch 处理，redirect 会被静默丢掉，
   * 「登录后回跳原页面」就失效了。
   */
  stringifySearch: (search) => {
    const record = search as Record<string, unknown>;
    const keys = Object.keys(record);
    /**
     * ⚠️ 花费总览必须**最先判**：它的 key 集合含 `to`，而 `to` 同时是概览 / 广告管理区间
     * 三件套的一员。不过判定用的是 `.every()`（**全部** key 都属本页），因此
     * `{to}` 不会命中本页、`{granularity,to}` 不会命中概览。这里仍放最前，语义最清晰。
     */
    const isSpendOverviewSearch =
      keys.length > 0 && keys.every((key) => SPEND_OVERVIEW_SEARCH_KEYS.includes(key));
    if (isSpendOverviewSearch) return spendOverviewSearchToQueryString(record);
    // ⚠️ 货号看板必须**先判**：它的唯一 URL 参数是 `language`，而 `language` 同时是
    // AdsSearch / AdManagementSearch 的字段。若不先判，`{language:"de"}` 会被当成
    // 广告管理（并补出 from/to 两个参数），链接就串页了。
    // 判据 = key 集合**只有** language（货号看板只写这一个参数）。
    const isStyleCodeBoardSearch =
      keys.length > 0 && keys.every((key) => STYLE_CODE_BOARD_SEARCH_KEYS.includes(key));
    if (isStyleCodeBoardSearch) return styleCodeBoardSearchToQueryString(record);
    /**
     * 经营概览：key 集合 = `from`/`to`/`range`（共享三件套），**必须与广告管理用同一套
     * 序列化规则**（`rangeSearchToParams`：from/to 总是写出、range 仅在全站默认档 last7 时省略），
     * 因此两个分支对共享 key 的输出**逐字一致**，识别顺序不会改变链接。
     */
    const isOverviewSearch =
      keys.length > 0 && keys.every((key) => OVERVIEW_SEARCH_KEYS.includes(key));
    if (isOverviewSearch) return overviewSearchToQueryString(record);
    // 广告管理：key 集合与 AdsSearch 完全不重叠，按自己的 schema 序列化（默认值省略）。
    const isAdManagementSearch =
      keys.length > 0 && keys.every((key) => AD_MANAGEMENT_SEARCH_KEYS.includes(key));
    if (isAdManagementSearch) return adManagementSearchToQueryString(record);
    /**
     * Google 系列：唯一 key 是 `gq` / `gview`（都带 `g` 前缀），与上面任何一页都不重叠；
     * 因此这一支放在最后、紧挨 AdsSearch 之前，识别顺序不会改变任何链接。
     */
    const isGoogleCampaignsSearch =
      keys.length > 0 && keys.every((key) => GOOGLE_CAMPAIGNS_SEARCH_KEYS.includes(key));
    if (isGoogleCampaignsSearch) return googleCampaignsSearchToQueryString(record);
    const isAdsSearch = keys.every((key) =>
      Object.prototype.hasOwnProperty.call(DEFAULT_ADS_SEARCH, key),
    );
    if (isAdsSearch) return adsSearchToQueryString(record);
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(record)) {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        params.set(key, String(value));
      }
    }
    const queryString = params.toString();
    return queryString.length > 0 ? `?${queryString}` : "";
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
